import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Container, Box, Typography, Button, Paper,
  ToggleButtonGroup, ToggleButton, CircularProgress, Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';

const SelectUserType = () => {
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        console.log("SelectUserType - User authenticated:", currentUser);
        setUser(currentUser);
        
        // Check if user already has a type set
        if (currentUser.attributes && currentUser.attributes['custom:user_type']) {
          const existingUserType = currentUser.attributes['custom:user_type'];
          console.log("User already has a type:", existingUserType);
          setUserType(existingUserType); // Set the current user type in state
          
          // Don't automatically redirect if they're already on the selection page
          // This prevents redirect loops
        }
      } catch (err) {
        console.error("Not authenticated, redirecting to login:", err);
        navigate('/login', { replace: true });
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  const handleUserTypeChange = (event, newUserType) => {
    if (newUserType !== null) {
      setUserType(newUserType);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userType) {
      setError('Please select a user type');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      if (!user) {
        const currentUser = await Auth.currentAuthenticatedUser();
        setUser(currentUser);
      }
      
      console.log("Updating user attributes with user type:", userType);
      
      // Update user attributes with the selected user type
      await Auth.updateUserAttributes(user, {
        'custom:user_type': userType
      });
      
      console.log("User type updated successfully");
      
      // Redirect based on user type
      if (userType === 'brand') {
        navigate('/brand', { replace: true });
      } else {
        navigate('/influencer', { replace: true });
      }
    } catch (err) {
      console.error('Error updating user attributes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Select Your Role
          </Typography>
          
          <Typography variant="body1" align="center" sx={{ mb: 4 }}>
            Please select how you want to use InfHire
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <ToggleButtonGroup
              value={userType}
              exclusive
              onChange={handleUserTypeChange}
              aria-label="user type"
              fullWidth
              sx={{ mb: 3 }}
            >
              <ToggleButton value="brand" aria-label="brand" sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1">I'm a Brand</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Looking to work with influencers
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="influencer" aria-label="influencer" sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1">I'm an Influencer</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Looking to work with brands
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !userType}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SelectUserType;
